import React, { useState } from 'react';
import { Button, Modal } from '@cloudclevr/components';
import { useIsMobile } from '@hooks/utils/isMobile';
import { useIsTablet } from '@hooks/utils/isTablet';

export interface ArrangeCallbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (notes?: string) => void;
  isSubmitting?: boolean;
}

export const ArrangeCallbackModal: React.FC<ArrangeCallbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [notes, setNotes] = useState('');
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();

   // Character limit validation
  const MAX_CHARS = 300;
  const isOverLimit = notes.length > MAX_CHARS;

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARS) {
      setNotes(value);
    }
  };


  const handleSubmit = () => {
    onSubmit(notes.trim() || undefined);
    setNotes('');
  };

  const handleClose = () => {
    setNotes('');
    onClose();
  };

  const modalSize = isTablet || isMobile ? 'small' : 'medium';
  const modalPosition = isMobile ? 'bottom' : 'default';

  return (
    <Modal
      testId="arrange-callback-modal"
      headerTxt="Arrange a call back"
      modalClassName="bg-green"
      isOpen={isOpen}
      size={'small'}
      position={modalPosition}
      showCloseBtn={true}
      onClose={handleClose}
      closeOnClickOutside={false}
      actionButtons={
        <div data-testid="action-buttons-container" className="flex flex-col-reverse gap-2 lg:flex-row lg:gap-3">
          
          <Button
            variant="primary"
            onClick={handleSubmit}
            testId="submit-callback-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Arranging...' : 'Arrange a call back'}
          </Button>
          <Button
            variant="outlineLight"
            onClick={handleClose}
            testId="cancel-callback-button"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      }
      contentClassName="flex !flex-col !items-start !justify-start bg-white"
    >
      <div data-testid="modal-content" className="w-full space-y-4">
        <p data-testid="modal-description" className="text-gray-700 text-base">
          One of our team will email you to arrange a time to call.
        </p>

         <div data-testid="notes-section" className="space-y-2">
          <label
            htmlFor="callback-notes"
            data-testid="notes-label"
            className="block text-sm font-semibold text-gray-900"
          >
            Optional: Anything we need to know before we call?
          </label>
          <textarea
            id="callback-notes"
            data-testid="callback-notes-textarea"
            value={notes}
            onChange={handleNotesChange}
            placeholder="Tell us if there's a good time to call, or anything specific you'd like to discuss."
            className={`w-full min-h-[120px] px-4 py-3 text-gray-900 placeholder-gray-400 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent ${
              isOverLimit ? 'border-red-500' : 'border-gray-300'
            }`}
            disabled={isSubmitting}
            maxLength={MAX_CHARS}
          />
          <div className="flex justify-between items-center text-sm">
            <div>
              {isOverLimit && (
                <span data-testid="character-limit-warning" className="text-red-600">
                  Character limit exceeded
                </span>
              )}
            </div>
            <span data-testid="character-count" className={`${isOverLimit ? 'text-red-600' : 'text-gray-500'}`}>
              {notes.length}/{MAX_CHARS}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ArrangeCallbackModal;